import { pointDistanceSquared } from './pointDistanceSquared.js';
import { Point } from './types.js';

/**
    @desc square distance from a point to a segment
    @param {[number, number]} p - point
    @param {[number, number]} p1 - segmentAnchor1
    @param {[number, number]} p2 - segmentAnchor2
    @private
*/
function getSqSegDist(p: [number, number], p1: [number, number], p2: [number, number]): number {
  let x: number = p1[0],
    y: number = p1[1];

  let dx: number = p2[0] - x,
    dy: number = p2[1] - y;

  if (dx !== 0 || dy !== 0) {
    const t: number = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);

    if (t > 1) {
      x = p2[0];
      y = p2[1];
    } else if (t > 0) {
      x += dx * t;
      y += dy * t;
    }
  }

  dx = p[0] - x;
  dy = p[1] - y;

  return dx * dx + dy * dy;
}

/**
    @desc basic distance-based simplification
    @param {Point[]} poly
    @param {number} sqTolerance
    @private
*/
function simplifyRadialDist(poly: Point[], sqTolerance: number): Point[] {
  let point: Point = poly[0];
  let prevPoint: Point = poly[0];

  const newPoints: Point[] = [prevPoint];

  for (let i = 1, len = poly.length; i < len; i++) {
    point = poly[i];

    if (pointDistanceSquared(point, prevPoint) > sqTolerance) {
      newPoints.push(point);
      prevPoint = point;
    }
  }

  if (prevPoint !== point) newPoints.push(point);

  return newPoints;
}

/**
    @param {Point[]} polygon
    @param {number} first
    @param {number} last
    @param {number} sqTolerance
    @param {Point[]} simplified
    @private
*/
function simplifyDPStep(
  poly: Point[],
  first: number,
  last: number,
  sqTolerance: number,
  simplified: Point[]
): void {
  let index: number = 0,
    maxSqDist: number = sqTolerance;

  for (let i: number = first + 1; i < last; i++) {
    const sqDist: number = getSqSegDist(poly[i], poly[first], poly[last]);

    if (sqDist > maxSqDist) {
      index = i;
      maxSqDist = sqDist;
    }
  }

  if (maxSqDist > sqTolerance) {
    if (index - first > 1) simplifyDPStep(poly, first, index, sqTolerance, simplified);
    simplified.push(poly[index]);
    if (last - index > 1) simplifyDPStep(poly, index, last, sqTolerance, simplified);
  }
}

/**
    @desc simplification using Ramer-Douglas-Peucker algorithm
    @param {Array} polygon
    @param {Number} sqTolerance
    @private
*/
function simplifyDouglasPeucker(poly: Point[], sqTolerance: number): Point[] {
  const last: number = poly.length - 1;

  const simplified: Point[] = [poly[0]];
  simplifyDPStep(poly, 0, last, sqTolerance, simplified);
  simplified.push(poly[last]);

  return simplified;
}

export const simplify = (
  poly: Point[],
  tolerance: number = 1,
  highestQuality: boolean = false
): Point[] => {
  if (poly.length <= 2) return poly;

  const sqTolerance: number = tolerance * tolerance;

  poly = highestQuality ? poly : simplifyRadialDist(poly, sqTolerance);
  poly = simplifyDouglasPeucker(poly, sqTolerance);

  return poly;
};
